import { StoreLite } from '@mailok/store-lite';
import { combineLatest, Observable } from 'rxjs';
import { IDescription } from '../../../core/models';
import { map, startWith } from 'rxjs/operators';

export interface MarkState<T extends IDescription> {
    selected: T[];
    elements: T[];
    filter: string;
}

export class MarkStateStore<T extends IDescription> extends StoreLite<MarkState<T>> {
    constructor() {
        super({
            elements: [],
            selected: [],
            filter: ''
        });
    }

    selected$: Observable<T[]> = this.select<T[]>(store => store.selected);
    filter$: Observable<string> = this.select<string>(store => store.filter);
    elements$: Observable<any[]> = this.select<any[]>(store => store.elements);
    filterElements$ = combineLatest([this.elements$.pipe(startWith([])), this.filter$.pipe(startWith(''))]).pipe(
        map(([elements, filter]) =>
            elements.filter(element => {
                let geofenceTypes: any = [
                    { id: 1, value: 'base' },
                    { id: 2, value: 'carga' },
                    { id: 3, value: 'descarga' },
                    { id: 4, value: 'carga y descarga' },
                    { id: 5, value: 'siembra' },
                    { id: 6, value: 'cosecha' },
                    { id: 9999, value: 'generica' }
                ];
                if (filter === '') {
                    return elements;
                } else {
                    if (element.geofence_type) {
                        let getIdFromFilter = geofenceTypes.findIndex(element =>
                            element.value.includes(
                                filter
                                    .toLowerCase()
                                    .normalize('NFD')
                                    .replace(/[\u0300-\u036f]/g, '')
                            )
                        );
                        if (getIdFromFilter !== -1) {
                            return element.geofence_type == geofenceTypes[getIdFromFilter].id;
                        } else {
                            return (<IDescription>element).description
                                .toLocaleLowerCase()
                                .includes(filter.toLocaleLowerCase());
                        }
                    } else {
                        return (<IDescription>element).description
                            .toLocaleLowerCase()
                            .includes(filter.toLocaleLowerCase());
                    }
                }
            })
        )
    );

    setElements(elements: T[]): void {
        this.update(state => ({ selected: [], filter: state.filter, elements }));
    }

    addElement(element: T): void {
        this.update(state => ({ ...state, elements: [...state.elements, element] }));
    }

    addSelectElement(selectedElement: T, selected: boolean, idKey: string) {
        if (selected) {
            this.addSelected(selectedElement);
        } else {
            this.removeSelected(selectedElement, idKey);
        }
    }

    setSelected(selected: T[]): void {
        this.update(state => ({ ...state, selected }));
    }

    addSelected(selected: T): void {
        this.update(state => ({ ...state, selected: [...state.selected, selected] }));
    }

    removeSelected(selected: T, idKey: string): void {
        const filtered = this.getSelected().filter(select => select[idKey] !== selected[idKey]);
        this.update(state => ({
            ...state,
            selected: filtered
        }));
    }

    removeElement(selected: T, idKey: string): void {
        this.update(state => ({
            ...state,
            elements: state.elements.filter(element => element[idKey] !== selected[idKey])
        }));
    }

    removeElements(selected: T[], idKey: string): void {
        for (let i = 0; i < selected.length; i++) {
            this.removeElement(selected[i], idKey);
        }
    }

    setFilter(filter: string): void {
        this.update(state => ({ ...state, filter }));
    }

    getFilter(): string {
        return this.selectSnapshot(store => store.filter);
    }

    getElements(): T[] {
        return this.selectSnapshot(store => store.elements);
    }

    getSelected(): T[] {
        return this.selectSnapshot(store => store.selected);
    }

    editElement(element: T, idKey: string) {
        this.update(state => ({
            ...state,
            elements: state.elements.map(el => this.updateOneElement(el, element, idKey)),
            selected: state.selected.map(el => this.updateOneElement(el, element, idKey))
        }));
    }

    updateOneElement(current: T, element: T, idKey: string): T {
        if (current[idKey] === element[idKey]) {
            return element;
        } else {
            return current;
        }
    }

    clear() {
        this.update({
            elements: [],
            selected: [],
            filter: ''
        });
    }
}
